import * as React from 'react';
import { useEffect, useImperativeHandle, useMemo, useRef, forwardRef } from 'react';
import carTopSprite from '../../../assets/images/car-top-led.png';
import { detectGapsBefore } from './trailGaps';

// Animated replay of one run's GPS trail: the trail draws itself progressively
// (glow + core line over a faint dotted "ahead" line) while a top-down car
// sprite — with the campaign's ad creative composited onto its rooftop LED
// screen — drives along it. All per-frame work happens imperatively against
// google.maps objects and DOM nodes; React only re-renders on prop changes.

// Where the blank LED panel sits inside the car sprite (fractions of the
// sprite box, measured from the generated asset — see assets/images/car-top-led.png).
const SCREEN_RECT = { left: 0.265, top: 0.312, width: 0.47, height: 0.373 };
const SPRITE_ASPECT = 232 / 512; // sprite width / height
const BASE_REPLAY_MS = 60000; // one full run replays in 60s at 1× speed

export interface TrailReplayHandle {
    // p in [0, 1] along the run's recorded time span
    seek: (p: number) => void;
}

interface ITrailReplayProps {
    map: google.maps.Map;
    trackPoints: any[];
    color: string;
    adMediaUrl?: string | null;
    adIsVideo?: boolean;
    playing: boolean;
    speed: number;
    // wallClockMs is null when the trail has no usable real timestamps
    onTick?: (progress: number, elapsedMs: number, totalMs: number, wallClockMs: number | null) => void;
    onEnded?: () => void;
}

const toRad = (d: number) => (d * Math.PI) / 180;

// Initial bearing from a to b in degrees, 0 = north, clockwise
const bearingDeg = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
    const dLng = toRad(b.lng - a.lng);
    const y = Math.sin(dLng) * Math.cos(toRad(b.lat));
    const x =
        Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
        Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLng);
    return (Math.atan2(y, x) * 180) / Math.PI;
};

// Signed shortest angular delta from a to b, in (-180, 180]
const shortestAngle = (a: number, b: number) => {
    let d = (b - a) % 360;
    if (d > 180) d -= 360;
    if (d < -180) d += 360;
    return d;
};

// Fast planar distance approximation, good enough for timing weights
const approxDist = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
    const dx = (b.lng - a.lng) * Math.cos(toRad((a.lat + b.lat) / 2));
    const dy = b.lat - a.lat;
    return Math.sqrt(dx * dx + dy * dy);
};

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

// One-time keyframes for the car's pulsing halo
let haloCssInjected = false;
const injectHaloCss = () => {
    if (haloCssInjected || typeof document === 'undefined') return;
    haloCssInjected = true;
    const el = document.createElement('style');
    el.textContent = `
@keyframes advCarHalo {
  0% { transform: translate(-50%, -50%) scale(0.55); opacity: 0.65; }
  100% { transform: translate(-50%, -50%) scale(1.7); opacity: 0; }
}`;
    document.head.appendChild(el);
};

export const TrailReplay = forwardRef<TrailReplayHandle, ITrailReplayProps>((props, ref) => {
    const { map, trackPoints, color, adMediaUrl, adIsVideo, playing, speed, onTick, onEnded } = props;

    // Normalize points once: lat/lng list + a monotonic relative-time axis.
    // Trails can come from the viewer screen or the driver app; timestamps may
    // be epoch seconds, epoch millis, missing, or non-monotonic — fall back to
    // distance-proportional timing when they're unusable.
    const prepared = useMemo(() => {
        const pts = (trackPoints || []).filter(
            (tp: any) => Number.isFinite(tp?.latitude) && Number.isFinite(tp?.longitude)
        );
        const ll = pts.map((tp: any) => ({ lat: tp.latitude, lng: tp.longitude }));
        const raw = pts.map((tp: any) => Number(tp?.timestamp) || 0);
        const scale = raw[0] > 1e11 ? 1 : 1000; // epoch millis vs epoch seconds
        let t: number[] = [];
        let prev = 0;
        for (let i = 0; i < raw.length; i++) {
            const rel = (raw[i] - raw[0]) * scale;
            prev = Math.max(prev, rel); // clamp out-of-order fixes
            t.push(prev);
        }
        let total = t[t.length - 1] || 0;
        let startEpochMs: number | null = raw[0] > 1e9 ? raw[0] * scale : null;
        if (total < 10000) {
            // No usable timing — weight by distance instead
            let acc = 0;
            t = ll.map((p, i) => {
                if (i > 0) acc += approxDist(ll[i - 1], p);
                return acc;
            });
            total = acc || 1;
            startEpochMs = null;
        }
        // Recording gaps split the trail: gapBefore[i] marks the segment
        // (i-1 → i) as a hole in the data, and rangeOf[i] assigns each point
        // to its contiguous recorded stretch so the traveled polylines never
        // paint a solid line across a gap.
        const gapBefore = detectGapsBefore(pts);
        const rangeOf: number[] = [];
        let range = 0;
        for (let i = 0; i < ll.length; i++) {
            if (i > 0 && gapBefore[i]) range++;
            rangeOf.push(range);
        }
        return { ll, t, total, startEpochMs, gapBefore, rangeOf, rangeCount: range + 1 };
    }, [trackPoints]);

    // Active campaigns poll every ~10s and hand us a brand-new trackPoints array
    // even when nothing moved. Rebuild the map objects only when the trail's
    // *content* changes, never on identity alone — otherwise a live replay
    // restarts and the camera jumps back to the start on every poll.
    const contentKey = `${prepared.ll.length}:${Math.round(prepared.total)}:${prepared.ll[0]?.lat ?? 0},${prepared.ll[0]?.lng ?? 0}`;

    // Imperative handles built once per mount
    // Chase-cam: keep the car centered while playing. A manual drag hands the
    // camera back to the user; play/seek re-engages the follow.
    const followRef = useRef(true);

    const objsRef = useRef<{
        glowLines: google.maps.Polyline[]; // one traveled pair per recorded stretch
        coreLines: google.maps.Polyline[];
        gapLines: google.maps.Polyline[]; // dotted connectors over recording gaps
        ahead: google.maps.Polyline;
        gPts: google.maps.LatLng[];
        overlay: any;
        media: HTMLImageElement | HTMLVideoElement | null;
    } | null>(null);
    const fixedCountRef = useRef(0); // trail points fully committed to the traveled polylines
    const headRangeRef = useRef<number | null>(null); // range whose polylines carry the interpolated head
    const progressRef = useRef(0);
    const prevTotalRef = useRef(0); // total of the trail the current progress was measured against
    const headingRef = useRef(0);
    const endedFiredRef = useRef(false);
    const lastTickAtRef = useRef(0);

    const playingRef = useRef(playing);
    playingRef.current = playing;
    const speedRef = useRef(speed);
    speedRef.current = speed;
    const onTickRef = useRef(onTick);
    onTickRef.current = onTick;
    const onEndedRef = useRef(onEnded);
    onEndedRef.current = onEnded;

    // Position + segment index for a progress value
    const sample = (p: number) => {
        const { ll, t, total } = prepared;
        const tm = clamp(p, 0, 1) * total;
        let lo = 0;
        let hi = t.length - 1;
        while (lo < hi - 1) {
            const mid = (lo + hi) >> 1;
            if (t[mid] <= tm) lo = mid;
            else hi = mid;
        }
        const span = t[hi] - t[lo];
        const f = span > 0 ? (tm - t[lo]) / span : 0;
        const pos = {
            lat: ll[lo].lat + (ll[hi].lat - ll[lo].lat) * f,
            lng: ll[lo].lng + (ll[hi].lng - ll[lo].lng) * f
        };
        // Heading from the surrounding segment; skip zero-length segments
        let heading = headingRef.current;
        for (let i = lo; i < ll.length - 1; i++) {
            if (approxDist(ll[i], ll[i + 1]) > 1e-7) {
                heading = bearingDeg(ll[i], ll[i + 1]);
                break;
            }
        }
        return { pos, segIdx: lo, heading, elapsedMs: tm };
    };

    // Grow (or rebuild after a backwards seek) the traveled polylines up to
    // segIdx, then place the interpolated head point. Each recorded stretch
    // has its own polyline pair; while the car crosses a recording gap the
    // head is not appended anywhere, so the dotted connector stays visible.
    const syncTraveled = (segIdx: number, interp: google.maps.LatLngLiteral) => {
        const objs = objsRef.current;
        if (!objs) return;
        const { rangeOf, gapBefore } = prepared;
        const paths = (k: number) => [objs.glowLines[k].getPath(), objs.coreLines[k].getPath()];
        if (headRangeRef.current != null) {
            paths(headRangeRef.current).forEach((path) => path.pop()); // drop previous head
            headRangeRef.current = null;
        }
        if (segIdx + 1 < fixedCountRef.current) {
            // Backwards seek — rebuild every traveled path from scratch
            for (let k = 0; k < objs.glowLines.length; k++) paths(k).forEach((path) => path.clear());
            for (let i = 0; i <= segIdx; i++) paths(rangeOf[i]).forEach((path) => path.push(objs.gPts[i]));
        } else {
            for (let i = fixedCountRef.current; i <= segIdx; i++) {
                paths(rangeOf[i]).forEach((path) => path.push(objs.gPts[i]));
            }
        }
        fixedCountRef.current = segIdx + 1;
        if (!gapBefore[segIdx + 1]) {
            const head = new google.maps.LatLng(interp.lat, interp.lng);
            paths(rangeOf[segIdx]).forEach((path) => path.push(head));
            headRangeRef.current = rangeOf[segIdx];
        }
    };

    const applyFrame = (p: number, snapHeading: boolean, dtMs: number) => {
        const objs = objsRef.current;
        if (!objs) return;
        const { pos, segIdx, heading, elapsedMs } = sample(p);
        if (snapHeading) {
            headingRef.current = heading;
        } else {
            headingRef.current += shortestAngle(headingRef.current, heading) * clamp(dtMs / 180, 0, 1);
        }
        syncTraveled(segIdx, pos);
        objs.overlay.update(pos, headingRef.current);
        if (followRef.current && (playingRef.current || snapHeading)) {
            map.setCenter(pos);
        }
        const now = performance.now();
        if (now - lastTickAtRef.current > 100 || p >= 1 || snapHeading) {
            lastTickAtRef.current = now;
            const wall = prepared.startEpochMs != null ? prepared.startEpochMs + elapsedMs : null;
            onTickRef.current?.(p, elapsedMs, prepared.total, wall);
        }
    };

    useImperativeHandle(ref, () => ({
        seek: (p: number) => {
            progressRef.current = clamp(p, 0, 1);
            endedFiredRef.current = progressRef.current >= 1;
            followRef.current = true;
            applyFrame(progressRef.current, true, 0);
        }
    }));

    // Build map objects + car overlay; torn down when the run (or map) changes
    useEffect(() => {
        if (!map || prepared.ll.length < 2 || typeof google === 'undefined') return;
        injectHaloCss();

        const gPts = prepared.ll.map((p) => new google.maps.LatLng(p.lat, p.lng));

        // Faint dotted preview of the road ahead
        const ahead = new google.maps.Polyline({
            map,
            path: gPts,
            strokeOpacity: 0,
            zIndex: 11,
            icons: [
                {
                    icon: { path: 'M 0,-0.5 0,0.5', strokeOpacity: 0.45, strokeWeight: 2.5, strokeColor: color, scale: 1.6 },
                    offset: '0',
                    repeat: '12px'
                }
            ]
        });
        // Traveled trail: soft glow under a solid core — one pair per recorded
        // stretch so recording gaps never get painted over with a solid line
        const glowLines: google.maps.Polyline[] = [];
        const coreLines: google.maps.Polyline[] = [];
        for (let k = 0; k < prepared.rangeCount; k++) {
            glowLines.push(
                new google.maps.Polyline({
                    map,
                    path: [],
                    strokeColor: color,
                    strokeOpacity: 0.3,
                    strokeWeight: 11,
                    zIndex: 12
                })
            );
            coreLines.push(
                new google.maps.Polyline({
                    map,
                    path: [],
                    strokeColor: color,
                    strokeOpacity: 1,
                    strokeWeight: 4.5,
                    zIndex: 13
                })
            );
        }
        // Dotted connectors over recording gaps — always visible, marking
        // "no data was recorded between these two points"
        const gapLines: google.maps.Polyline[] = [];
        for (let i = 1; i < prepared.ll.length; i++) {
            if (!prepared.gapBefore[i]) continue;
            gapLines.push(
                new google.maps.Polyline({
                    map,
                    path: [gPts[i - 1], gPts[i]],
                    strokeOpacity: 0,
                    zIndex: 12,
                    icons: [
                        {
                            icon: {
                                path: 'M 0,-0.1 0,0.1',
                                strokeOpacity: 0.8,
                                strokeWeight: 3.5,
                                strokeColor: '#94a3b8',
                                scale: 2
                            },
                            offset: '0',
                            repeat: '12px'
                        }
                    ]
                })
            );
        }

        // Custom overlay: halo + rotating car sprite with the ad on its roof
        class CarOverlay extends google.maps.OverlayView {
            root = document.createElement('div');
            rotator = document.createElement('div');
            halo = document.createElement('div');
            adBox = document.createElement('div');
            media: HTMLImageElement | HTMLVideoElement | null = null;
            pos: google.maps.LatLngLiteral | null = null;
            heading = 0;
            carH = 0;

            constructor() {
                super();
                const { root, rotator, halo, adBox } = this;
                root.style.cssText =
                    'position:absolute;top:0;left:0;will-change:transform;pointer-events:none;';
                halo.style.cssText =
                    `position:absolute;top:0;left:0;border-radius:50%;border:2px solid ${color};` +
                    'animation:advCarHalo 2s ease-out infinite;';
                rotator.style.cssText =
                    'position:absolute;top:0;left:0;transform-origin:50% 50%;' +
                    'filter:drop-shadow(0 3px 6px rgba(0,0,0,0.35));';
                const car = document.createElement('img');
                car.src = carTopSprite;
                car.alt = '';
                car.draggable = false;
                car.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;';
                adBox.style.cssText =
                    `position:absolute;left:${SCREEN_RECT.left * 100}%;top:${SCREEN_RECT.top * 100}%;` +
                    `width:${SCREEN_RECT.width * 100}%;height:${SCREEN_RECT.height * 100}%;` +
                    `overflow:hidden;border-radius:8%;box-shadow:0 0 8px 1px ${color}90;` +
                    'background:linear-gradient(135deg,#7c3aed,#4f46e5);';
                if (adMediaUrl) {
                    const media = adIsVideo ? document.createElement('video') : document.createElement('img');
                    if (media instanceof HTMLVideoElement) {
                        media.muted = true;
                        media.loop = true;
                        media.autoplay = true;
                        media.playsInline = true;
                    }
                    media.src = adMediaUrl;
                    // Ad creative is landscape; the rooftop screen reads along the
                    // car's length, so rotate the media 90° inside the panel.
                    media.style.cssText =
                        'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(90deg);object-fit:cover;';
                    this.media = media;
                    adBox.appendChild(media);
                }
                rotator.appendChild(car);
                rotator.appendChild(adBox);
                root.appendChild(halo);
                root.appendChild(rotator);
            }

            onAdd() {
                this.getPanes()?.overlayMouseTarget.appendChild(this.root);
            }

            onRemove() {
                this.root.remove();
            }

            layout(carH: number) {
                if (carH === this.carH) return;
                this.carH = carH;
                const carW = carH * SPRITE_ASPECT;
                this.rotator.style.width = `${carW}px`;
                this.rotator.style.height = `${carH}px`;
                this.rotator.style.left = `${-carW / 2}px`;
                this.rotator.style.top = `${-carH / 2}px`;
                const haloD = carH * 0.95;
                this.halo.style.width = `${haloD}px`;
                this.halo.style.height = `${haloD}px`;
                if (this.media) {
                    // Swapped dimensions so the rotated media fills the panel
                    this.media.style.width = `${carH * SCREEN_RECT.height}px`;
                    this.media.style.height = `${carW * SCREEN_RECT.width}px`;
                }
            }

            draw() {
                const proj = this.getProjection();
                if (!proj || !this.pos) return;
                const zoom = (this.getMap() as google.maps.Map | null)?.getZoom() ?? 14;
                this.layout(clamp(76 * Math.pow(1.18, zoom - 14), 44, 130));
                const px = proj.fromLatLngToDivPixel(new google.maps.LatLng(this.pos.lat, this.pos.lng));
                if (!px) return;
                this.root.style.transform = `translate(${px.x}px, ${px.y}px)`;
                this.rotator.style.transform = `rotate(${this.heading}deg)`;
            }

            update(pos: google.maps.LatLngLiteral, heading: number) {
                this.pos = pos;
                this.heading = heading;
                this.draw();
            }
        }

        const overlay = new CarOverlay();
        overlay.setMap(map);

        const dragListener = map.addListener('dragstart', () => {
            followRef.current = false;
        });

        objsRef.current = { glowLines, coreLines, gapLines, ahead, gPts, overlay, media: overlay.media };
        fixedCountRef.current = 0;
        headRangeRef.current = null;
        // A live run keeps growing while it replays: carry the elapsed time
        // across rebuilds so the car stays where it was instead of restarting.
        const prevTotal = prevTotalRef.current;
        if (prevTotal > 0 && prepared.total > 0 && prevTotal !== prepared.total) {
            progressRef.current = clamp((progressRef.current * prevTotal) / prepared.total, 0, 1);
        }
        prevTotalRef.current = prepared.total;
        endedFiredRef.current = progressRef.current >= 1;
        applyFrame(progressRef.current, true, 0);

        return () => {
            dragListener.remove();
            glowLines.forEach((l) => l.setMap(null));
            coreLines.forEach((l) => l.setMap(null));
            gapLines.forEach((l) => l.setMap(null));
            ahead.setMap(null);
            overlay.setMap(null);
            objsRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, contentKey, color]);

    // Keep the presigned URL fresh without rebuilding the overlay
    useEffect(() => {
        const media = objsRef.current?.media;
        if (media && adMediaUrl && media.src !== adMediaUrl) media.src = adMediaUrl;
    }, [adMediaUrl]);

    // Animation loop — advances progress only while `playing`
    useEffect(() => {
        let raf = 0;
        let last = performance.now();
        const frame = (now: number) => {
            const dt = Math.min(now - last, 100);
            last = now;
            if (playingRef.current && objsRef.current) {
                if (progressRef.current < 1) {
                    progressRef.current = Math.min(
                        1,
                        progressRef.current + (dt * speedRef.current) / BASE_REPLAY_MS
                    );
                    applyFrame(progressRef.current, false, dt);
                }
                if (progressRef.current >= 1 && !endedFiredRef.current) {
                    endedFiredRef.current = true;
                    onEndedRef.current?.();
                }
            }
            raf = requestAnimationFrame(frame);
        };
        raf = requestAnimationFrame(frame);
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, prepared]);

    // Pressing play re-engages the chase-cam; from the end it also rewinds
    useEffect(() => {
        if (!playing) return;
        followRef.current = true;
        if (progressRef.current >= 1) {
            progressRef.current = 0;
            endedFiredRef.current = false;
        }
        applyFrame(progressRef.current, true, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playing]);

    return null;
});

TrailReplay.displayName = 'TrailReplay';
